import { z } from "zod/v4";
import { defineResource, defineEndpoint } from "@visioni/a3s-client-api";
import { FEATURE_NAME } from "../constants";
import { ExportId } from "../plugins/roadsense/export-slot";
import { ScopedLivePayloadPath } from "./get-scoped-roadsense-live-payload";

/**
 * On-request export of one RoadSense site's workbook for the caller's camera
 * scope, over the live dashboard's window up to the current 15-minute slot.
 * The app POSTs a request, gets an `exportId` back (202) and polls the status
 * route until the workbook is ready to download. The stored report and its
 * daily artefacts are never touched.
 *
 * `exportId` is the slot's UTC cutoff as `YYYYMMDDTHHmmZ` (e.g.
 * `20261103T0345Z`), not a random id: a repeat request in the same slot gets
 * the same id and reuses the same workbook.
 */

/**
 * `defineEndpoint`'s POST overload always validates a body, so the request
 * takes an empty JSON object (`{}`), as the regenerate routes do.
 */
export const RequestScopedRoadsenseExportBody = z.object({}).strict();
export type RequestScopedRoadsenseExportBody = z.output<typeof RequestScopedRoadsenseExportBody>;

export const RequestScopedRoadsenseExportResponse = z.object({
  data: z.object({ exportId: ExportId }),
});
export type RequestScopedRoadsenseExportResponse = z.output<typeof RequestScopedRoadsenseExportResponse>;

/**
 * POST /v2/tenants/{tenantId}/features/reports/roadsense/reports/{reportId}/sites/{siteId}/export
 *
 * Responds 202 with the export's id. No report, a non-RoadSense report, a
 * missing site and a site outside the caller's scope are one identical 404, as
 * on the scoped live route.
 */
export const scopedRoadsenseExportResource = defineResource({
  feature: FEATURE_NAME,
  resource: "roadsense/reports/{reportId}/sites/{siteId}/export",
  resourcePathSchema: ScopedLivePayloadPath,
  scope: "tenant",
});

export const requestScopedRoadsenseExportEndpoint = defineEndpoint(scopedRoadsenseExportResource, {
  method: "POST",
  bodySchema: RequestScopedRoadsenseExportBody,
  responseSchema: RequestScopedRoadsenseExportResponse,
});

/**
 * `exportId` becomes an S3 key segment, so it must name a real 15-minute slot
 * before the handler sees it.
 */
export const ScopedRoadsenseExportPath = ScopedLivePayloadPath.extend({
  exportId: ExportId,
});
export type ScopedRoadsenseExportPath = z.output<typeof ScopedRoadsenseExportPath>;

export const ScopedRoadsenseExportStatusResponse = z.object({
  data: z.discriminatedUnion("status", [
    z.object({ status: z.literal("pending") }),
    z.object({ status: z.literal("failed") }),
    z.object({
      status: z.literal("ready"),
      /** Presigned GET for the `.xlsx`; it carries the customer-facing filename. */
      url: z.string(),
      expiresInSeconds: z.int().positive(),
      /** ISO-8601 UTC instant the workbook covers up to. */
      cutoff: z.iso.datetime(),
    }),
  ]),
});
export type ScopedRoadsenseExportStatusResponse = z.output<typeof ScopedRoadsenseExportStatusResponse>;

/**
 * GET /v2/tenants/{tenantId}/features/reports/roadsense/reports/{reportId}/sites/{siteId}/export/{exportId}
 */
export const scopedRoadsenseExportStatusResource = defineResource({
  feature: FEATURE_NAME,
  resource: "roadsense/reports/{reportId}/sites/{siteId}/export/{exportId}",
  resourcePathSchema: ScopedRoadsenseExportPath,
  scope: "tenant",
});

export const getScopedRoadsenseExportStatusEndpoint = defineEndpoint(scopedRoadsenseExportStatusResource, {
  responseSchema: ScopedRoadsenseExportStatusResponse,
});
